import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { MapPin, RefreshCw, Plus, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EventCard = ({ ev, isAdmin, onDelete, isNew }) => {
  const d = new Date(ev.start);
  return (
    <div className="hb-card p-4 md:p-5 flex gap-4 md:gap-5 rise" data-testid={`event-${ev.id}`}>
      <div className="w-14 md:w-16 shrink-0 rounded-2xl bg-[var(--hb-lime)] text-center py-2.5 self-start">
        <div className="font-accent text-[11px] font-bold uppercase">{format(d, "MMM")}</div>
        <div className="font-display text-3xl leading-none">{format(d, "d")}</div>
        <div className="font-accent text-[10px] mt-0.5">{format(d, "EEE")}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-accent">
          {isNew && (
            <span className="mr-2 bg-[var(--hb-ink)] !text-[var(--hb-lime)] px-1.5 py-0.5 rounded" data-testid={`event-new-badge-${ev.id}`}>New</span>
          )}
          {ev.time_label || format(d, "h:mm a")}{ev.source === "website" ? " · ohheyboo.com" : ""}
        </div>
        <h3 className="font-display text-lg md:text-xl mt-1 leading-snug">{ev.title}</h3>
        {ev.location && (
          <div className="flex items-center gap-1.5 text-sm text-[var(--hb-muted)] mt-1"><MapPin size={14} />{ev.location}</div>
        )}
        {ev.description && <p className="text-sm mt-2 line-clamp-3 whitespace-pre-wrap">{ev.description}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          {(ev.rsvp_url || ev.url) && (ev.end || ev.start) >= new Date().toISOString() && (
            <a href={ev.rsvp_url || ev.url} target="_blank" rel="noreferrer" className="btn-primary !py-2" data-testid={`event-rsvp-${ev.id}`}>
              RSVP <ExternalLink size={13} />
            </a>
          )}
          {ev.url && ev.url !== ev.rsvp_url && (
            <a href={ev.url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`event-link-${ev.id}`}>
              Details
            </a>
          )}
          {isAdmin && ev.source === "manual" && (
            <button className="btn-ghost" onClick={() => onDelete(ev)} data-testid={`event-delete-${ev.id}`}><Trash2 size={13} /></button>
          )}
        </div>
      </div>
      {ev.image_url && <img src={ev.image_url} alt={ev.title} className="hidden sm:block w-24 md:w-36 self-start rounded-xl object-cover border border-[var(--hb-line)]" data-testid={`event-image-${ev.id}`} />}
    </div>
  );
};

const AddEvent = ({ open, setOpen, onAdded }) => {
  const [f, setF] = useState({ title: "", start: "", end: "", location: "", url: "", description: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, start: new Date(f.start).toISOString(), end: f.end ? new Date(f.end).toISOString() : "" };
      onAdded((await api.post("/events", payload)).data);
      setOpen(false);
      toast.success("Event added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white" data-testid="add-event-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add an event</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input className="hb-input" placeholder="Event title" required value={f.title} onChange={set("title")} data-testid="event-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs label-accent">Starts<input type="datetime-local" className="hb-input mt-1" required value={f.start} onChange={set("start")} data-testid="event-start-input" /></label>
            <label className="text-xs label-accent">Ends<input type="datetime-local" className="hb-input mt-1" value={f.end} onChange={set("end")} data-testid="event-end-input" /></label>
          </div>
          <input className="hb-input" placeholder="Location or Zoom" value={f.location} onChange={set("location")} data-testid="event-location-input" />
          <input className="hb-input" placeholder="Link (RSVP / Zoom URL)" value={f.url} onChange={set("url")} data-testid="event-url-input" />
          <textarea className="hb-input" placeholder="Description" value={f.description} onChange={set("description")} data-testid="event-description-input" />
          <button className="btn-primary w-full" data-testid="event-save-button">Save event</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function EventsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [data, setData] = useState(null);
  const [view, setView] = useState("upcoming");
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const { setNewEvents } = useOutletContext();
  const seenAt = useRef(null);
  const load = () => api.get("/events").then((r) => {
    if (seenAt.current === null) {
      seenAt.current = r.data.seen_at;
      api.post("/events/seen").then(() => setNewEvents(0)).catch(() => {});
    }
    setData(r.data);
  });
  useEffect(() => {
    load();
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/events/sync");
      toast.success(`Synced ${r.data.synced} event(s) from ohheyboo.com`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${ev.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const now = new Date().toISOString();
  const list = (data?.events || []).filter((e) => (view === "upcoming" ? (e.end || e.start) >= now : (e.end || e.start) < now));
  if (view === "past") list.reverse();

  return (
    <div data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-accent">Be in the room</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Events</h1>
          {data?.last_sync && (
            <p className="text-xs text-[var(--hb-muted)] mt-2" data-testid="events-last-sync">
              Synced with ohheyboo.com {timeAgo(data.last_sync)}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={sync} disabled={syncing} data-testid="events-sync-button">
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> Sync website
            </button>
            <button className="btn-primary" onClick={() => setOpen(true)} data-testid="events-add-button"><Plus size={14} /> Add event</button>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-6">
        {["upcoming", "past"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`events-view-${v}`}
            className={`px-4 py-1.5 rounded-full text-sm capitalize border ${view === v ? "bg-[var(--hb-ink)] text-white border-[var(--hb-ink)]" : "bg-white border-[var(--hb-line)]"}`}>
            {v}
          </button>
        ))}
      </div>
      <div className="space-y-4 mt-6">
        {data === null && <div className="text-[var(--hb-muted)]">Loading events...</div>}
        {data && list.length === 0 && (
          <div className="hb-card p-10 text-center" data-testid="events-empty">
            <p className="font-display text-xl">No {view} events right now.</p>
            <p className="text-sm text-[var(--hb-muted)] mt-1">New events published on ohheyboo.com show up here automatically.</p>
          </div>
        )}
        {list.map((ev) => (
          <EventCard key={ev.id} ev={ev} isAdmin={isAdmin} onDelete={remove}
            isNew={!!seenAt.current && ev.created_at > seenAt.current && view === "upcoming"} />
        ))}
      </div>
      {isAdmin && <AddEvent open={open} setOpen={setOpen} onAdded={() => load()} />}
    </div>
  );
}
